import { FC, useMemo } from 'react';
import { Box, Container, SimpleGrid } from '@chakra-ui/react';
import { FarmFragment } from '../../../generated/types';
import { Metatags } from '../../Layout/Metatags';
import { FarmSubCategoryList } from './FarmSubCategoryList';
import { FarmPremiumMenu } from './FarmPremiumMenu';
import { FarmHeader } from './FarmHeader';
import { FarmRatings } from './FarmRatings';
import { ParagraphRenderer } from '../../Paragraph/ParagraphRenderer';
import { FarmImages } from './FarmImages';
import { FarmDescription } from './FarmDescription';
import { FarmNearPremiumFarms } from './FarmNearPremiumFarms';
import { FarmCategoryListGrouped } from './FarmCategoryListGrouped';
import { FarmContact } from './FarmContact';
import { FarmContext } from '../../../context/FarmContext';
import { StaticAdSlot } from '../../Common/StaticAdSlot';

interface FarmProps {
  data: FarmFragment;
}

export const Farm: FC<FarmProps> = ({ data }) => {
  const isAddress = data.listingType.title === 'Address';
  const isBasic = data.listingType.title === 'Basic';
  const isPremium = data.listingType.title === 'Premium';

  const adjustedMetatags = useMemo(() => {
    const metatagsWithTitleTransformed = data.metatags.map((tag) => {
      // we have readonly properties, so create a new one here to be able to adjust
      const newTag = {
        ...tag,
        attributes: {
          ...tag.attributes,
        },
      };
      if (newTag.tag === 'title') {
        newTag.attributes.content = `${data.title} in ${data.address.postcode} ${data.address.city}`;
      }

      return newTag;
    });

    // check if we have description metatag
    const descriptionMetaTag = metatagsWithTitleTransformed.find(
      (tag) => tag.tag === 'meta' && tag.attributes.name === 'description'
    );

    let description = data.title;
    const categories = data.categories.filter(
      (category) => category.category.title === 'Betriebsart'
    );
    if (categories.length > 0) {
      description += ' | ';
      categories.forEach((category, i) => {
        category.subCategories?.forEach((subCategory, j) => {
          description += `✓ ${subCategory.title}`;

          if (j !== category.subCategories.length - 1) {
            description += ` `;
          }
        });
      });
    }

    description += ' | ► Jetzt hier mehr erfahren!';

    if (descriptionMetaTag) {
      descriptionMetaTag.attributes.content = description;
    } else {
      metatagsWithTitleTransformed.push({
        __typename: 'Metatag',
        tag: 'meta',
        attributes: {
          __typename: 'MetatagAttribute',
          name: 'description',
          content: description,
        },
      });
    }

    return metatagsWithTitleTransformed;
  }, [data]);

  return (
    <>
      <Metatags metatags={adjustedMetatags} />

      <FarmHeader data={data} />
      {isPremium && <FarmPremiumMenu data={data} />}

      {isAddress && (
        <Container maxW="container.main" py={6}>
          <FarmCategoryListGrouped
            categories={data.categories.filter(
              (category) => category.category.title === 'Betriebsart'
            )}
          />
        </Container>
      )}

      {isAddress && <StaticAdSlot adKey="in_content_1" />}
      {isAddress && <FarmContact data={data} />}
      {isAddress && <StaticAdSlot adKey="in_content_2" />}

      {data.content?.length > 0 && (
        <FarmContext.Provider
          value={{
            farm: data,
          }}
        >
          <ParagraphRenderer paragraphs={data.content} />
        </FarmContext.Provider>
      )}

      {isAddress && <FarmNearPremiumFarms data={data} />}

      {!isAddress && (
        <Container maxW="container.main" py={6}>
          {isBasic && <FarmImages data={data} />}
          {isBasic && <FarmDescription data={data} />}
        </Container>
      )}

      {isBasic && (
        <Container maxW="container.main" py={6}>
          <FarmCategoryListGrouped categories={data.categories} />
        </Container>
      )}

      <FarmRatings data={data} />

      {!isAddress && <FarmContact data={data} />}
    </>
  );
};
